import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import ResponsiveDialog from '../components/ResponsiveDialog';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Science as ScienceIcon,
  CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import type {
  CityTransportApiConfig,
  CityTransportMapping,
  PreviewFetchResponse,
  PreviewLinesResponse,
  GtfsImportPayload,
  GtfsImportResponse,
  OgcFeaturesImportPayload,
  GtfsImportRow,
  GtfsBrowseDataset,
  GtfsBrowseResponse,
} from '../types/cityTransportApi';
import CityTransportMapPicker from '../components/CityTransportMapPicker';

const EMPTY_FORM: CityTransportApiConfig = {
  city: '',
  countryCode: 'FR',
  mode: 'bus',
  latitude: null,
  longitude: null,
  providerName: '',
  baseUrl: '',
  headers: {},
  params: { limit: 100 },
  mapping: {
    root: 'results',
    fields: {
      id: 'id',
      name: 'name',
      line: 'line',
      type: 'type',
    },
  },
  enabled: true,
};

const RENNES_MAPPING_EXAMPLE = `{
  "root": "results",
  "fields": {
    "id": "code",
    "name": "libellelong",
    "line": "nomcourtligne",
    "type": "type",
    "direction": "senscommercial",
    "color": "couleurtrace"
  }
}`;

const NANTES_MAPPING_EXAMPLE = `{
  "root": "results",
  "fields": {
    "id": "route_id",
    "name": "route_long_name",
    "line": "route_short_name",
    "type": "route_type",
    "color": "route_color"
  }
}`;

function stringifyJson(v: unknown, fallback: string): string {
  if (v === undefined || v === null) return fallback;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return fallback;
  }
}

/** Message d’erreur renvoyé par l’API Symfony / Axios (pour diagnostic). */
function getApiErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return 'Erreur réseau ou serveur.';
  }
  const ax = error as {
    response?: { status?: number; data?: { error?: string; message?: string; detail?: string } };
  };
  const status = ax.response?.status;
  const data = ax.response?.data;
  if (typeof data?.error === 'string' && data.error.trim() !== '') {
    return data.error;
  }
  if (typeof data?.detail === 'string' && data.detail.trim() !== '') {
    return data.detail;
  }
  if (typeof data?.message === 'string' && data.message.trim() !== '') {
    return data.message;
  }
  if (status === 403) {
    return 'Accès refusé : le compte doit avoir le rôle super administrateur (ROLE_SUPER_ADMIN). Vérifiez les rôles de l’utilisateur en base de données.';
  }
  if (status === 409) {
    return 'Une configuration existe déjà pour cette ville, ce pays et ce mode (bus / métro / …). Modifiez l’existant ou choisissez un autre mode.';
  }
  if (status === 400) {
    return 'Requête invalide (vérifiez ville, pays, URL, mode).';
  }
  if (status === 401) {
    return 'Session expirée ou non authentifié — reconnectez-vous.';
  }
  return `Erreur serveur${status ? ` (${status})` : ''}.`;
}

function safeParseMapping(json: string): CityTransportMapping {
  try {
    const o = JSON.parse(json) as unknown;
    if (o && typeof o === 'object' && !Array.isArray(o)) {
      return o as CityTransportMapping;
    }
  } catch {
    /* ignore */
  }
  return { fields: {} };
}

const MAPPING_UI_FIELDS: {
  key: keyof NonNullable<CityTransportMapping['fields']>;
  label: string;
  helper: string;
}[] = [
  { key: 'id', label: 'Identifiant (id)', helper: 'Clé technique unique de la ligne' },
  { key: 'line', label: 'Numéro / court (line)', helper: 'Ex. route_short_name, nomcourtligne' },
  { key: 'name', label: 'Libellé long (name)', helper: 'Ex. route_long_name, libellelong' },
  { key: 'type', label: 'Type (type)', helper: 'Bus, tram, métro…' },
  { key: 'direction', label: 'Direction / sens', helper: 'Terminus, sens commercial…' },
  { key: 'color', label: 'Couleur', helper: 'Code couleur affichage' },
];

const CityTransportPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CityTransportApiConfig | null>(null);
  const [form, setForm] = useState<CityTransportApiConfig>(EMPTY_FORM);
  const [headersJson, setHeadersJson] = useState('{}');
  const [paramsJson, setParamsJson] = useState('{"limit": 100}');
  const [mappingJson, setMappingJson] = useState(stringifyJson(EMPTY_FORM.mapping, '{}'));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [shapeAnalysis, setShapeAnalysis] = useState<PreviewFetchResponse | null>(null);
  const [linesPreview, setLinesPreview] = useState<PreviewLinesResponse | null>(null);
  const [linesPreviewLoading, setLinesPreviewLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CityTransportApiConfig | null>(null);

  const [gtfsCitiesText, setGtfsCitiesText] = useState('');
  const [gtfsCountryCode, setGtfsCountryCode] = useState('FR');
  const [gtfsMaxResources, setGtfsMaxResources] = useState(3);
  const [gtfsMaxPages, setGtfsMaxPages] = useState(5);
  const [gtfsTag, setGtfsTag] = useState('gtfs');
  const [ogcUrl, setOgcUrl] = useState('');
  const [ogcCity, setOgcCity] = useState('');
  const [ogcCountry, setOgcCountry] = useState('FR');
  const [ogcMode, setOgcMode] = useState('bus');
  const [ogcProvider, setOgcProvider] = useState('');
  const [gtfsLastResult, setGtfsLastResult] = useState<GtfsImportResponse | null>(null);
  const [deleteGtfsTarget, setDeleteGtfsTarget] = useState<GtfsImportRow | null>(null);
  const [transportSaveError, setTransportSaveError] = useState<string | null>(null);

  const [browseQ, setBrowseQ] = useState('');
  const [browseRegion, setBrowseRegion] = useState('');
  const [browseDepartment, setBrowseDepartment] = useState('');
  const [browseCommune, setBrowseCommune] = useState('');
  const [hideCoveredCities, setHideCoveredCities] = useState(true);
  const [browseSource, setBrowseSource] = useState<'datagouv' | 'transport'>('transport');
  const [browsePage, setBrowsePage] = useState(1);
  const [browseResult, setBrowseResult] = useState<GtfsBrowseResponse | null>(null);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseImportTarget, setBrowseImportTarget] = useState<GtfsBrowseDataset | null>(null);
  const [browseImportCity, setBrowseImportCity] = useState('');

  React.useEffect(() => {
    if (!apiService.isAuthenticated()) {
      navigate('/login');
    }
  }, [navigate]);

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ['city-transport-apis'],
    queryFn: () => apiService.getCityTransportApis(),
  });

  const { data: gtfsRows = [], isLoading: gtfsLoading } = useQuery({
    queryKey: ['city-transport-gtfs-imports'],
    queryFn: () => apiService.getCityTransportGtfsImports(),
  });

  const { data: configuredTransport } = useQuery({
    queryKey: ['configured-transport-cities'],
    queryFn: () => apiService.getConfiguredTransportCities(),
  });

  const { data: transportFacets } = useQuery({
    queryKey: ['transport-facets', browseRegion, browseDepartment],
    queryFn: () =>
      apiService.getTransportPanFacets({
        region: browseRegion.trim() || undefined,
        department: browseDepartment.trim() || undefined,
      }),
    enabled: browseSource === 'transport',
    staleTime: 5 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: (payload: CityTransportApiConfig) => apiService.createCityTransportApi(payload),
    onSuccess: () => {
      setTransportSaveError(null);
      queryClient.invalidateQueries({ queryKey: ['city-transport-apis'] });
      closeDialog();
    },
    onError: (err: unknown) => {
      setTransportSaveError(getApiErrorMessage(err));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<CityTransportApiConfig> }) =>
      apiService.updateCityTransportApi(id, payload),
    onSuccess: () => {
      setTransportSaveError(null);
      queryClient.invalidateQueries({ queryKey: ['city-transport-apis'] });
      closeDialog();
    },
    onError: (err: unknown) => {
      setTransportSaveError(getApiErrorMessage(err));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteCityTransportApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['city-transport-apis'] });
      setDeleteTarget(null);
    },
  });

  const importGtfsMutation = useMutation({
    mutationFn: (payload: GtfsImportPayload) => apiService.importCityTransportGtfs(payload),
    onSuccess: (data) => {
      setGtfsLastResult(data);
      queryClient.invalidateQueries({ queryKey: ['city-transport-gtfs-imports'] });
      queryClient.invalidateQueries({ queryKey: ['configured-transport-cities'] });
      queryClient.invalidateQueries({ queryKey: ['city-transport-apis'] });
      setBrowseImportTarget(null);
    },
  });

  const importOgcFeaturesMutation = useMutation({
    mutationFn: (payload: OgcFeaturesImportPayload) => apiService.importCityTransportOgcFeatures(payload),
    onSuccess: (data) => {
      setGtfsLastResult(data);
      queryClient.invalidateQueries({ queryKey: ['city-transport-gtfs-imports'] });
      queryClient.invalidateQueries({ queryKey: ['configured-transport-cities'] });
      queryClient.invalidateQueries({ queryKey: ['city-transport-apis'] });
    },
  });

  const deleteGtfsMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteCityTransportGtfsImport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['city-transport-gtfs-imports'] });
      queryClient.invalidateQueries({ queryKey: ['configured-transport-cities'] });
      setDeleteGtfsTarget(null);
    },
  });

  const openCreate = () => {
    setTransportSaveError(null);
    setEditing(null);
    setForm(EMPTY_FORM);
    setHeadersJson('{}');
    setParamsJson('{"limit": 100}');
    setMappingJson(stringifyJson(EMPTY_FORM.mapping, '{}'));
    setJsonError(null);
    setPreviewResult(null);
    setShapeAnalysis(null);
    setLinesPreview(null);
    setDialogOpen(true);
  };

  const openEdit = (row: CityTransportApiConfig) => {
    setTransportSaveError(null);
    setEditing(row);
    setForm({
      ...row,
      mode: row.mode || 'bus',
      latitude: row.latitude ?? null,
      longitude: row.longitude ?? null,
    });
    setHeadersJson(stringifyJson(row.headers, '{}'));
    setParamsJson(stringifyJson(row.params, '{"limit": 100}'));
    setMappingJson(stringifyJson(row.mapping, '{}'));
    setJsonError(null);
    setPreviewResult(null);
    setShapeAnalysis(null);
    setLinesPreview(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setTransportSaveError(null);
    setJsonError(null);
    setPreviewResult(null);
    setShapeAnalysis(null);
    setLinesPreview(null);
  };

  const parseJsonFields = (): {
    headers: CityTransportApiConfig['headers'];
    params: CityTransportApiConfig['params'];
    mapping: CityTransportApiConfig['mapping'];
  } | null => {
    setJsonError(null);
    try {
      const headers =
        headersJson.trim() === ''
          ? null
          : (JSON.parse(headersJson) as Record<string, string>);
      const params =
        paramsJson.trim() === '' ? null : (JSON.parse(paramsJson) as Record<string, unknown>);
      const mapping = mappingJson.trim() === '' ? null : JSON.parse(mappingJson);
      const h =
        headers && Object.keys(headers).length ? headers : null;
      return {
        headers: h,
        params: params as CityTransportApiConfig['params'],
        mapping: mapping as CityTransportApiConfig['mapping'],
      };
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : 'JSON invalide');
      return null;
    }
  };

  const handleSave = () => {
    setTransportSaveError(null);
    const parsed = parseJsonFields();
    if (!parsed) return;
    const cc = (form.countryCode || 'FR').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
    const countryCode = cc.length === 2 ? cc : 'FR';
    const payload: CityTransportApiConfig = {
      ...form,
      countryCode,
      headers: parsed.headers ?? null,
      params: parsed.params ?? null,
      mapping: parsed.mapping ?? null,
    };
    if (editing?.id) {
      updateMutation.mutate({ id: editing.id, payload });
    } else {
      const { id: _id, ...createBody } = payload;
      createMutation.mutate(createBody as CityTransportApiConfig);
    }
  };

  const handlePreview = async () => {
    const parsed = parseJsonFields();
    if (!parsed) return;
    setPreviewLoading(true);
    setPreviewResult(null);
    setShapeAnalysis(null);
    setLinesPreview(null);
    try {
      const headers = parsed.headers ?? undefined;
      const params = (parsed.params ?? {}) as Record<string, string | number | boolean>;
      const root =
        parsed.mapping && typeof parsed.mapping === 'object' && 'root' in parsed.mapping
          ? (parsed.mapping as { root?: string }).root
          : undefined;
      const res = await apiService.previewCityTransportFetch({
        baseUrl: form.baseUrl,
        headers: headers ?? undefined,
        params,
        root,
      });
      setShapeAnalysis(res);
      setPreviewResult(JSON.stringify(res, null, 2));
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? JSON.stringify((e as { response?: { data?: unknown } }).response?.data)
          : String(e);
      setPreviewResult(`Erreur: ${msg}`);
      setShapeAnalysis(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const parsedMapping = useMemo(() => safeParseMapping(mappingJson), [mappingJson]);

  const discoveredKeys = shapeAnalysis?.firstRecordKeys ?? [];

  const rootSuggestions = useMemo(() => {
    const fromApi = shapeAnalysis?.topLevelArrays ?? [];
    const keys = fromApi.map((x) => x.key);
    const r = shapeAnalysis?.root;
    if (r && !keys.includes(r)) {
      return [r, ...keys];
    }
    return keys.length ? keys : r ? [r] : [];
  }, [shapeAnalysis]);

  const commitMapping = (next: CityTransportMapping) => {
    setMappingJson(stringifyJson(next, '{}'));
    setJsonError(null);
  };

  const setRootPath = (root: string) => {
    const cur = safeParseMapping(mappingJson);
    const trimmed = root.trim();
    commitMapping({
      ...cur,
      root: trimmed || undefined,
    });
  };

  const setFieldPath = (
    key: keyof NonNullable<CityTransportMapping['fields']>,
    value: string
  ) => {
    const cur = safeParseMapping(mappingJson);
    const fields = { ...(cur.fields ?? {}) };
    const t = value.trim();
    if (!t) {
      delete fields[key];
    } else {
      fields[key] = t;
    }
    commitMapping({ ...cur, fields });
  };

  const handlePreviewLines = async () => {
    const parsed = parseJsonFields();
    if (!parsed) return;
    if (!parsed.mapping || typeof parsed.mapping !== 'object') {
      setJsonError('Mapping invalide : vérifiez le JSON ou les champs ci-dessous.');
      return;
    }
    setLinesPreviewLoading(true);
    setLinesPreview(null);
    setJsonError(null);
    try {
      const headers = parsed.headers ?? undefined;
      const params = (parsed.params ?? {}) as Record<string, string | number | boolean>;
      const res = await apiService.previewCityTransportLines({
        baseUrl: form.baseUrl,
        headers,
        params,
        mapping: parsed.mapping as CityTransportMapping,
      });
      setLinesPreview(res);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? JSON.stringify((e as { response?: { data?: unknown } }).response?.data)
          : String(e);
      setLinesPreview({
        httpStatus: 0,
        totalNormalized: 0,
        lines: [],
        error: msg,
      });
    } finally {
      setLinesPreviewLoading(false);
    }
  };

  const handleGtfsImport = () => {
    const cities = gtfsCitiesText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (cities.length === 0) {
      return;
    }
    importGtfsMutation.mutate({
      cities,
      countryCode: gtfsCountryCode,
      maxResourcesPerCity: gtfsMaxResources,
      maxApiPages: gtfsMaxPages,
      tag: gtfsTag.trim() || 'gtfs',
    });
  };

  const handleOgcFeaturesImport = () => {
    const url = ogcUrl.trim();
    const city = ogcCity.trim();
    if (url === '' || city === '') {
      return;
    }
    importOgcFeaturesMutation.mutate({
      url,
      city,
      countryCode: ogcCountry.toUpperCase().slice(0, 2),
      mode: ogcMode,
      providerName: ogcProvider.trim() || undefined,
    });
  };

  const runBrowse = async (page: number) => {
    setBrowseLoading(true);
    try {
      const r = await apiService.browseGtfsDatasets({
        q: browseQ.trim() || undefined,
        region: browseSource === 'transport' ? browseRegion.trim() || undefined : undefined,
        department: browseSource === 'transport' ? browseDepartment.trim() || undefined : undefined,
        commune: browseSource === 'transport' ? browseCommune.trim() || undefined : undefined,
        page,
        page_size: 15,
        tag: gtfsTag.trim() || 'gtfs',
        source: browseSource,
        hide_covered: browseSource === 'transport' ? hideCoveredCities : undefined,
      });
      setBrowseResult(r);
      setBrowsePage(page);
    } finally {
      setBrowseLoading(false);
    }
  };

  const openBrowseImport = (row: GtfsBrowseDataset) => {
    setBrowseImportTarget(row);
    setBrowseImportCity((row.suggested_city || row.title).slice(0, 120));
  };

  const confirmBrowseImport = () => {
    if (!browseImportTarget || !browseImportCity.trim()) return;
    importGtfsMutation.mutate({
      datasets: [{ datasetId: browseImportTarget.id, city: browseImportCity.trim() }],
      countryCode: gtfsCountryCode,
      maxResourcesPerDataset: gtfsMaxResources,
    });
  };

  const errMsg = useMemo(() => {
    if (!error) return null;
    const ax = error as { response?: { status?: number; data?: { error?: string; message?: string } } };
    if (ax.response?.status === 403) {
      return 'Accès refusé : compte super administrateur requis.';
    }
    return ax.response?.data?.error || ax.response?.data?.message || 'Erreur de chargement';
  }, [error]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Transports par ville (Open Data)
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Ajoutez <strong>une configuration par type</strong> (bus, métro, tram, train) pour la même ville : plusieurs
        lignes avec le même nom de ville et des URLs différentes. La carte définit le centre affiché côté app. Le{' '}
        <strong>mapping</strong> aligne le JSON Open Data sur les champs normalisés. Utilisez <strong>Analyser l’URL</strong>{' '}
        puis <strong>Aperçu liste</strong>.
      </Typography>

      {errMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errMsg}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Villes déjà couvertes (API + GTFS)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            L’identifiant <strong>ville</strong> doit être le <strong>même texte</strong> que celui envoyé par les annonces
            (champ « ville », souvent identique à l’adresse). Cette liste se met à jour après chaque import ou config manuelle.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(configuredTransport?.cities ?? []).map((c) => (
              <Chip
                key={`${c.city}-${c.countryCode}`}
                size="small"
                label={`${c.city} (${c.countryCode}) · ${c.sources.join(' + ')}`}
                variant="outlined"
              />
            ))}
            {(configuredTransport?.cities ?? []).length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Aucune ville pour l’instant — importez un GTFS ou ajoutez une config API ci‑dessous.
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Parcourir les jeux GTFS
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Catalogue national : <strong>transport.data.gouv.fr</strong> (recommandé — zones couvertes, ressources GTFS)
            ou <strong>data.gouv.fr</strong> (tag GTFS). Choisissez un jeu, indiquez l’identifiant ville exact utilisé
            dans l’app (champ « ville » des annonces), puis importez les lignes.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="browse-source-label">Source catalogue</InputLabel>
              <Select
                labelId="browse-source-label"
                label="Source catalogue"
                value={browseSource}
                onChange={(e) => {
                  setBrowseSource(e.target.value as 'datagouv' | 'transport');
                  setBrowseResult(null);
                  setBrowsePage(1);
                }}
              >
                <MenuItem value="transport">transport.data.gouv.fr</MenuItem>
                <MenuItem value="datagouv">data.gouv.fr (tag GTFS)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Mots-clés (optionnel)"
              value={browseQ}
              onChange={(e) => setBrowseQ(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
              placeholder="ex. STAR, Réseau urbain…"
            />
            {browseSource === 'transport' && (
              <>
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 200, flex: '1 1 180px' }}
                  options={transportFacets?.regions ?? []}
                  value={browseRegion || null}
                  onChange={(_, value) => {
                    setBrowseRegion(value ?? '');
                    setBrowseDepartment('');
                    setBrowseCommune('');
                    setBrowseResult(null);
                  }}
                  renderInput={(params) => <TextField {...params} label="Région" />}
                  clearOnEscape
                />
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 200, flex: '1 1 180px' }}
                  options={transportFacets?.departments ?? []}
                  value={browseDepartment || null}
                  onChange={(_, value) => {
                    setBrowseDepartment(value ?? '');
                    setBrowseCommune('');
                    setBrowseResult(null);
                  }}
                  disabled={!browseRegion && (transportFacets?.departments?.length ?? 0) === 0}
                  renderInput={(params) => (
                    <TextField {...params} label="Département" placeholder={browseRegion ? '' : 'Choisir une région'} />
                  )}
                  clearOnEscape
                />
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 220, flex: '1 1 200px' }}
                  options={transportFacets?.communes ?? []}
                  value={browseCommune}
                  onChange={(_, value) => {
                    setBrowseCommune(typeof value === 'string' ? value : value ?? '');
                    setBrowseResult(null);
                  }}
                  freeSolo
                  renderInput={(params) => (
                    <TextField {...params} label="Commune / EPCI" placeholder="ex. Rennes, Rennes Métropole…" />
                  )}
                  clearOnEscape
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hideCoveredCities}
                      onChange={(e) => {
                        setHideCoveredCities(e.target.checked);
                        setBrowseResult(null);
                      }}
                    />
                  }
                  label="Masquer les territoires déjà couverts (API + GTFS)"
                />
              </>
            )}
            <Button
              variant="outlined"
              onClick={() => runBrowse(1)}
              disabled={browseLoading}
              startIcon={browseLoading ? <CircularProgress size={18} /> : undefined}
            >
              Rechercher
            </Button>
            <Button
              variant="text"
              size="small"
              disabled={browseLoading || !browseResult?.next_page}
              onClick={() => runBrowse(browsePage + 1)}
            >
              Page suivante
            </Button>
            {browseResult?.total != null && (
              <Typography variant="caption" color="text.secondary">
                {browseResult.total} jeu(x) GTFS
                {browseResult.source === 'transport' ? ' (catalogue PAN)' : ''}
                {browseResult.hidden_covered_count != null && browseResult.hidden_covered_count > 0
                  ? ` · ${browseResult.hidden_covered_count} masqué(s) (déjà couverts)`
                  : ''}
              </Typography>
            )}
          </Box>
          {browseResult?.error && <Alert severity="warning">{browseResult.error}</Alert>}
          {browseResult && browseResult.datasets.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Titre du jeu</TableCell>
                    {browseSource === 'transport' && <TableCell>Zone couverte</TableCell>}
                    <TableCell width={100}>GTFS</TableCell>
                    <TableCell>Suggestion ville</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {browseResult.datasets.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Typography variant="body2">{row.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          id {row.id}
                        </Typography>
                        {row.gtfs_resources && row.gtfs_resources.length > 0 && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            {row.gtfs_resources.map((r) => r.title).join(' · ')}
                          </Typography>
                        )}
                      </TableCell>
                      {browseSource === 'transport' && (
                        <TableCell>
                          {(row.covered_areas ?? []).slice(0, 3).map((area) => (
                            <Chip key={area} size="small" label={area} sx={{ mr: 0.5, mb: 0.5 }} />
                          ))}
                        </TableCell>
                      )}
                      <TableCell>{row.zip_count}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.suggested_city}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          disabled={row.zip_count < 1 || importGtfsMutation.isPending}
                          onClick={() => openBrowseImport(row)}
                        >
                          Importer…
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Import GTFS manuel (recherche par nom de ville)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Alternative : saisir des noms de villes — data.gouv est interrogé avec le tag GTFS + ces mots. Moins précis que le
            choix d’un jeu ci‑dessus. Les modes déjà couverts par une configuration API manuelle sont ignorés. Données servies
            sur <code>/api/transports/modes</code> et <code>/api/transports/lines</code>.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 720 }}>
            <TextField
              label="Villes (une par ligne ou séparées par des virgules)"
              value={gtfsCitiesText}
              onChange={(e) => setGtfsCitiesText(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              placeholder={'Ex.\nRennes\nNantes'}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Pays (ISO-2)"
                value={gtfsCountryCode}
                onChange={(e) => setGtfsCountryCode(e.target.value.toUpperCase().slice(0, 2))}
                sx={{ width: 120 }}
              />
              <TextField
                label="Max. ZIP / ville"
                type="number"
                value={gtfsMaxResources}
                onChange={(e) => setGtfsMaxResources(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
                sx={{ width: 140 }}
                inputProps={{ min: 1, max: 20 }}
              />
              <TextField
                label="Pages API max."
                type="number"
                value={gtfsMaxPages}
                onChange={(e) => setGtfsMaxPages(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                sx={{ width: 140 }}
                inputProps={{ min: 1, max: 50 }}
              />
              <TextField
                label="Tag data.gouv"
                value={gtfsTag}
                onChange={(e) => setGtfsTag(e.target.value)}
                sx={{ width: 160 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={importGtfsMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <CloudDownloadIcon />}
                onClick={handleGtfsImport}
                disabled={importGtfsMutation.isPending}
              >
                Rechercher et importer
              </Button>
            </Box>
            {importGtfsMutation.isError && (
              <Alert severity="error">Échec de l’import (réseau, timeout ou erreur serveur).</Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Import GeoJSON (OGC API Features)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Données du type <code>FeatureCollection</code> (GeoServer / OpenLayers), avec géométries et attributs dans{' '}
            <code>properties</code> — non assimilables à une simple liste d&apos;objets JSON pour le mapping manuel
            ci‑dessous. L&apos;API télécharge l&apos;URL, fusionne les pages <code>rel=&quot;next&quot;</code> si besoin,
            et agrège les tronçons par ligne / sens / variante. Le flux Ilevia « traces de lignes » (Métropole
            européenne de Lille) est pris en charge — indiquez la même <strong>ville</strong> que dans les annonces
            (ex. <code>Lille</code>), et un mode d&apos;affichage (souvent bus pour un réseau urbain mixte). Les gros
            exports (<code>limit=-1</code>) peuvent prendre plusieurs minutes.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 900 }}>
            <TextField
              label="URL des features (JSON)"
              value={ogcUrl}
              onChange={(e) => setOgcUrl(e.target.value)}
              fullWidth
              placeholder="https://data.lillemetropole.fr/geoserver/ogc/features/v1/collections/…/items?f=application%2Fjson&limit=-1"
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Ville (?city=)"
                value={ogcCity}
                onChange={(e) => setOgcCity(e.target.value)}
                sx={{ minWidth: 200 }}
                placeholder="Lille"
              />
              <TextField
                label="Pays (ISO-2)"
                value={ogcCountry}
                onChange={(e) => setOgcCountry(e.target.value.toUpperCase().slice(0, 2))}
                sx={{ width: 120 }}
              />
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel id="ogc-mode-label">Mode</InputLabel>
                <Select
                  labelId="ogc-mode-label"
                  label="Mode"
                  value={ogcMode}
                  onChange={(e) => setOgcMode(String(e.target.value))}
                >
                  <MenuItem value="bus">Bus</MenuItem>
                  <MenuItem value="metro">Métro</MenuItem>
                  <MenuItem value="tram">Tram</MenuItem>
                  <MenuItem value="train">Train</MenuItem>
                  <MenuItem value="other">Autre</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Fournisseur (optionnel)"
                value={ogcProvider}
                onChange={(e) => setOgcProvider(e.target.value)}
                sx={{ minWidth: 220 }}
                placeholder="Ilevia"
              />
            </Box>
            <Button
              variant="contained"
              color="secondary"
              startIcon={importOgcFeaturesMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <CloudDownloadIcon />}
              onClick={handleOgcFeaturesImport}
              disabled={importOgcFeaturesMutation.isPending || ogcUrl.trim() === '' || ogcCity.trim() === ''}
            >
              Importer le GeoJSON
            </Button>
            {importOgcFeaturesMutation.isError && (
              <Alert severity="error">{getApiErrorMessage(importOgcFeaturesMutation.error)}</Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      {gtfsLastResult && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Dernier résultat d&apos;import (GTFS ou GeoJSON)
          </Typography>
          <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 280, overflow: 'auto' }}>
            {JSON.stringify(gtfsLastResult, null, 2)}
          </Box>
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Lignes importées — base GTFS / GeoJSON
          </Typography>
          {gtfsLoading ? (
            <CircularProgress size={28} />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ville</TableCell>
                    <TableCell>Pays</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Fournisseur</TableCell>
                    <TableCell>Lignes</TableCell>
                    <TableCell>Import</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gtfsRows.map((gr) => (
                    <TableRow key={gr.id}>
                      <TableCell>
                        <strong>{gr.city}</strong>
                      </TableCell>
                      <TableCell>{gr.countryCode}</TableCell>
                      <TableCell>
                        <Chip size="small" label={gr.mode} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>
                          {gr.providerName}
                        </Typography>
                      </TableCell>
                      <TableCell>{gr.linesCount}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {gr.importedAt ? new Date(gr.importedAt).toLocaleString('fr-FR') : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="error" onClick={() => setDeleteGtfsTarget(gr)} title="Supprimer">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {gtfsRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="text.secondary">
                          Aucune donnée importée. Utilisez l&apos;import GTFS ou le bloc GeoJSON ci‑dessus.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Exemples Rennes &amp; Nantes (API Explore v2.1)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle2" gutterBottom>
            Rennes — parcours bus STAR
          </Typography>
          <Typography variant="caption" component="pre" sx={{ display: 'block', whiteSpace: 'pre-wrap', mb: 2 }}>
            URL records :{' '}
            {`https://data.rennesmetropole.fr/api/explore/v2.1/catalog/datasets/parcours-des-lignes-de-bus-du-reseau-star/records`}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Mapping suggéré :
          </Typography>
          <Typography variant="caption" component="pre" sx={{ display: 'block', whiteSpace: 'pre-wrap', mb: 2 }}>
            {RENNES_MAPPING_EXAMPLE}
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Nantes — circuits TAN
          </Typography>
          <Typography variant="caption" component="pre" sx={{ display: 'block', whiteSpace: 'pre-wrap', mb: 2 }}>
            URL :{' '}
            {`https://data.nantesmetropole.fr/api/explore/v2.1/catalog/datasets/244400404_tan-circuits/records`}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Mapping suggéré :
          </Typography>
          <Typography variant="caption" component="pre" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
            {NANTES_MAPPING_EXAMPLE}
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Nouvelle configuration
        </Button>
      </Box>

      <Card>
        <CardContent>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ville</TableCell>
                    <TableCell>Pays</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Fournisseur</TableCell>
                    <TableCell>Activé</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id ?? `${row.city}-${row.mode}`}>
                      <TableCell>
                        <strong>{row.city}</strong>
                      </TableCell>
                      <TableCell>{row.countryCode}</TableCell>
                      <TableCell>
                        <Chip size="small" label={row.mode || 'bus'} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                          {row.providerName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.enabled ? 'Oui' : 'Non'}
                          color={row.enabled ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(row)} title="Modifier">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => row.id && setDeleteTarget(row)}
                          title="Supprimer"
                          disabled={!row.id}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography color="text.secondary">Aucune configuration. Créez-en une ou utilisez aussi l’admin Symfony (EasyAdmin).</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <ResponsiveDialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="lg">
        <DialogTitle>{editing ? 'Modifier la configuration' : 'Nouvelle configuration transport'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {jsonError && <Alert severity="error">{jsonError}</Alert>}
            {transportSaveError && <Alert severity="error">{transportSaveError}</Alert>}
            <TextField
              label="Ville (identifiant métier, ex. celui passé à ?city=)"
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Code pays (ISO 2)"
              value={form.countryCode}
              onChange={(e) => setForm((f) => ({ ...f, countryCode: e.target.value.toUpperCase().slice(0, 2) }))}
              inputProps={{ maxLength: 2 }}
              sx={{ maxWidth: 120 }}
            />
            <FormControl sx={{ maxWidth: 320 }}>
              <InputLabel id="ct-mode-label">Type de réseau</InputLabel>
              <Select
                labelId="ct-mode-label"
                label="Type de réseau"
                value={form.mode || 'bus'}
                onChange={(e) =>
                  setForm((f) => ({ ...f, mode: String(e.target.value) }))
                }
              >
                <MenuItem value="bus">Bus</MenuItem>
                <MenuItem value="metro">Métro</MenuItem>
                <MenuItem value="tram">Tramway</MenuItem>
                <MenuItem value="train">Train</MenuItem>
                <MenuItem value="other">Autre</MenuItem>
              </Select>
            </FormControl>
            <CityTransportMapPicker
              latitude={form.latitude ?? null}
              longitude={form.longitude ?? null}
              onChange={(lat, lng) => setForm((f) => ({ ...f, latitude: lat, longitude: lng }))}
            />
            <TextField
              label="Nom du fournisseur / réseau"
              value={form.providerName}
              onChange={(e) => setForm((f) => ({ ...f, providerName: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="URL de base (GET, sans query ou avec — params ci-dessous s’ajoutent)"
              value={form.baseUrl}
              onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
              fullWidth
              required
              multiline
              minRows={2}
            />
            <TextField
              label="Headers (JSON)"
              value={headersJson}
              onChange={(e) => setHeadersJson(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label="Query params (JSON, ex. limite Explore)"
              value={paramsJson}
              onChange={(e) => setParamsJson(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={previewLoading ? <CircularProgress size={18} /> : <ScienceIcon />}
                onClick={handlePreview}
                disabled={previewLoading || !form.baseUrl}
              >
                Analyser l’URL (clés JSON)
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={linesPreviewLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
                onClick={handlePreviewLines}
                disabled={linesPreviewLoading || !form.baseUrl}
              >
                Aperçu liste normalisée
              </Button>
            </Box>

            {shapeAnalysis?.hint && (
              <Alert severity={shapeAnalysis.firstRecordKeys?.length ? 'info' : 'warning'}>
                {shapeAnalysis.hint}
                {shapeAnalysis.arrayLength != null && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Enregistrements détectés sous la racine : {shapeAnalysis.arrayLength}
                  </Typography>
                )}
              </Alert>
            )}

            <Typography variant="subtitle2">Mapping — racine du tableau dans le JSON</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Cliquez une puce (tableaux listés à la racine) ou saisissez un chemin (notation pointée, ex.{' '}
              <code>data.records</code>).
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {(shapeAnalysis?.topLevelArrays ?? []).map((ta) => (
                <Chip
                  key={ta.key}
                  label={`${ta.key} (${ta.count})`}
                  size="small"
                  onClick={() => setRootPath(ta.key)}
                  color={parsedMapping.root === ta.key ? 'primary' : 'default'}
                  variant={parsedMapping.root === ta.key ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
            <TextField
              label="mapping.root"
              placeholder="ex. results"
              size="small"
              fullWidth
              value={parsedMapping.root ?? ''}
              onChange={(e) => setRootPath(e.target.value)}
              inputProps={{ list: 'city-transport-root-datalist' }}
            />
            <datalist id="city-transport-root-datalist">
              {rootSuggestions.map((k) => (
                <option key={k} value={k} />
              ))}
            </datalist>

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Champs JSON → colonnes normalisées
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Liste dérivée du <strong>premier enregistrement</strong> après analyse. Vous pouvez choisir une clé ou
              saisir un chemin (ex. <code>route.id</code>).
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              {MAPPING_UI_FIELDS.map((meta) => (
                <React.Fragment key={meta.key}>
                  <TextField
                    label={meta.label}
                    size="small"
                    fullWidth
                    value={(parsedMapping.fields?.[meta.key] as string) ?? ''}
                    onChange={(e) => setFieldPath(meta.key, e.target.value)}
                    helperText={
                      discoveredKeys.length ? meta.helper : `${meta.helper} — lancez d’abord « Analyser l’URL »`
                    }
                    inputProps={{ list: `city-transport-field-${meta.key}` }}
                  />
                  <datalist id={`city-transport-field-${meta.key}`}>
                    {discoveredKeys.map((k) => (
                      <option key={k} value={k} />
                    ))}
                  </datalist>
                </React.Fragment>
              ))}
            </Box>

            <Accordion defaultExpanded={false} sx={{ mt: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Mapping JSON brut (édition avancée)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  label="mapping"
                  value={mappingJson}
                  onChange={(e) => {
                    setMappingJson(e.target.value);
                    setJsonError(null);
                  }}
                  fullWidth
                  multiline
                  minRows={10}
                  sx={{ fontFamily: 'monospace' }}
                />
              </AccordionDetails>
            </Accordion>

            <FormControlLabel
              control={
                <Switch
                  checked={form.enabled}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                />
              }
              label="Activé"
            />

            {linesPreview?.error && (
              <Alert severity="error">Erreur aperçu liste : {linesPreview.error}</Alert>
            )}
            {linesPreview && !linesPreview.error && linesPreview.lines.length === 0 && (
              <Alert severity="warning">
                Aucune ligne après normalisation (vérifiez <strong>root</strong> et les champs, ou augmentez{' '}
                <strong>limit</strong> dans les params).
              </Alert>
            )}
            {linesPreview && linesPreview.lines.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Liste transport ({linesPreview.lines.length} affichée(s)
                  {linesPreview.totalNormalized > linesPreview.lines.length
                    ? ` sur ${linesPreview.totalNormalized}`
                    : ''}
                  {linesPreview.truncated ? ' — tronqué à 100 pour l’aperçu' : ''})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>id</TableCell>
                        <TableCell>line</TableCell>
                        <TableCell>name</TableCell>
                        <TableCell>type</TableCell>
                        <TableCell>direction</TableCell>
                        <TableCell>color</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {linesPreview.lines.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell>{row.id != null ? String(row.id) : '—'}</TableCell>
                          <TableCell>{row.line ?? '—'}</TableCell>
                          <TableCell>{row.name ?? '—'}</TableCell>
                          <TableCell>{row.type ?? '—'}</TableCell>
                          <TableCell>{row.direction ?? '—'}</TableCell>
                          <TableCell>{row.color ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Accordion defaultExpanded={false}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Réponse brute « Analyser l’URL » (debug)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {previewResult ? (
                  <TextField
                    label="JSON"
                    value={previewResult}
                    fullWidth
                    multiline
                    minRows={8}
                    InputProps={{ readOnly: true }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Lancez « Analyser l’URL » pour voir le détail.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Supprimer cette configuration ?</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteTarget?.city} ({deleteTarget?.countryCode}) — {deleteTarget?.providerName}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Annuler</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteTarget?.id && deleteMutation.mutate(deleteTarget.id)}
            disabled={deleteMutation.isPending}
          >
            Supprimer
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog open={!!browseImportTarget} onClose={() => setBrowseImportTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Confirmer l’import GTFS</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {browseImportTarget?.title}
          </Typography>
          <TextField
            label="Identifiant ville (comme le champ « ville » des annonces / ?city=)"
            value={browseImportCity}
            onChange={(e) => setBrowseImportCity(e.target.value)}
            fullWidth
            required
            helperText="Doit correspondre exactement au texte utilisé côté application (casse et accents)."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBrowseImportTarget(null)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={confirmBrowseImport}
            disabled={!browseImportCity.trim() || importGtfsMutation.isPending}
          >
            Importer
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog open={!!deleteGtfsTarget} onClose={() => setDeleteGtfsTarget(null)}>
        <DialogTitle>Supprimer cet import GTFS ?</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteGtfsTarget?.city} ({deleteGtfsTarget?.countryCode}) — mode {deleteGtfsTarget?.mode} —{' '}
            {deleteGtfsTarget?.linesCount} ligne(s)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteGtfsTarget(null)}>Annuler</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteGtfsTarget?.id && deleteGtfsMutation.mutate(deleteGtfsTarget.id)}
            disabled={deleteGtfsMutation.isPending}
          >
            Supprimer
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
};

export default CityTransportPage;
